/**
 * QA Swap Program Code Modal
 * Small popup for replacing a program code across all question-answer
 * artifacts, used when a program code gets renamed. Every QA tagged with the
 * current code is re-tagged with the updated code, and each change is
 * recorded in the audit history.
 */

import React, { useEffect, useState } from 'react';
import { advisorApiService } from '../../services/advisor-api';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/QADetailModal.css';

interface QASwapProgramCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwapped?: () => void; // Callback after a successful swap
}

export const QASwapProgramCodeModal: React.FC<QASwapProgramCodeModalProps> = ({
  isOpen,
  onClose,
  onSwapped,
}) => {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successCount, setSuccessCount] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);

  const [currentCode, setCurrentCode] = useState('');
  const [updatedCode, setUpdatedCode] = useState('');
  const [availableProgramCodes, setAvailableProgramCodes] = useState<string[]>([]);

  const canUpdate = user?.role === 'user' || user?.role === 'admin';

  useEffect(() => {
    if (isOpen) {
      setCurrentCode('');
      setUpdatedCode('');
      setError(null);
      setSuccessCount(null);
      setConfirming(false);

      advisorApiService
        .getProgramCodes()
        .then((res) => {
          if (res.data) {
            // Union of official mapping codes and codes actually present on
            // Q&As, so stray/legacy codes can still be selected and cleaned up.
            const union = [
              ...new Set([
                ...res.data.program_codes,
                ...(res.data.program_codes_in_use ?? []),
              ]),
            ].sort();
            setAvailableProgramCodes(union);
          }
        })
        .catch((err) => {
          console.error('Failed to fetch program codes:', err);
          setError('Failed to load program codes');
        });
    }
  }, [isOpen]);

  const trimmedUpdated = updatedCode.trim().toUpperCase();
  const sameCode = !!currentCode && currentCode === trimmedUpdated;
  const isSwapDisabled =
    isSaving || !canUpdate || !currentCode || trimmedUpdated.length === 0 || sameCode;

  const handleSwap = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const response = await advisorApiService.swapQAProgramCode(currentCode, trimmedUpdated);

      if (response.data) {
        setSuccessCount(response.data.updated_count);
        setConfirming(false);
        if (onSwapped) {
          onSwapped();
        }
      } else {
        setError(response.error || 'Failed to update program code');
        setConfirming(false);
      }
    } catch (err) {
      console.error('Failed to swap program code:', err);

      let errorMessage = 'Failed to update program code';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as {
          response?: { data?: { error?: string }; status?: number };
          message?: string;
        };

        if (axiosError.response?.status === 404) {
          errorMessage = `'${currentCode}' was not found on any Q&A or in the code mapping`;
        } else if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      }

      setError(errorMessage);
      setConfirming(false);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="qa-modal-overlay" onClick={onClose}>
      <div
        className="qa-modal-content"
        style={{ maxWidth: '480px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="qa-modal-header">
          <h2>Update Program Code</h2>
          <button
            type="button"
            className="qa-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="qa-modal-body">
          {error && <div className="qa-modal-error">{error}</div>}

          {successCount !== null ? (
            <div className="qa-swap-success">
              <div className="qa-swap-success-title">Program code updated</div>
              <div className="qa-swap-success-detail">
                {successCount === 0 ? (
                  <>
                    No Q&As were tagged with <span className="qa-swap-code">{currentCode}</span>;
                    the official program code was renamed to{' '}
                    <span className="qa-swap-code">{trimmedUpdated}</span>.
                  </>
                ) : (
                  <>
                    {successCount} Q&A{successCount === 1 ? '' : 's'} switched from{' '}
                    <span className="qa-swap-code">{currentCode}</span> to{' '}
                    <span className="qa-swap-code">{trimmedUpdated}</span>. The change has been
                    recorded in the history.
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              <p className="qa-swap-hint">
                Replaces a program code on every Q&A that uses it — for when a program code gets
                renamed. Each change is logged in the history.
              </p>

              <div className="qa-swap-row">
                <div className="qa-swap-field">
                  <label className="qa-swap-label" htmlFor="qa-swap-current">
                    Current code
                  </label>
                  <select
                    id="qa-swap-current"
                    className="qa-swap-select"
                    value={currentCode}
                    onChange={(e) => setCurrentCode(e.target.value)}
                    disabled={isSaving}
                  >
                    <option value="">Select…</option>
                    {availableProgramCodes.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="qa-swap-arrow" aria-hidden="true">
                  →
                </div>

                <div className="qa-swap-field">
                  <label className="qa-swap-label" htmlFor="qa-swap-updated">
                    Updated code
                  </label>
                  <input
                    id="qa-swap-updated"
                    className="qa-swap-input"
                    type="text"
                    value={updatedCode}
                    onChange={(e) => setUpdatedCode(e.target.value)}
                    placeholder="e.g. EMCOMS"
                    maxLength={20}
                    disabled={isSaving}
                    list="qa-swap-code-options"
                  />
                  <datalist id="qa-swap-code-options">
                    {availableProgramCodes.map((code) => (
                      <option key={code} value={code} />
                    ))}
                  </datalist>
                </div>
              </div>

              {sameCode && (
                <p className="qa-swap-warning">The updated code must be different.</p>
              )}

              {confirming && (
                <p className="qa-swap-warning">
                  Replace <span className="qa-swap-code">{currentCode}</span> with{' '}
                  <span className="qa-swap-code">{trimmedUpdated}</span> on all associated Q&As?
                </p>
              )}
            </>
          )}
        </div>

        <div className="qa-modal-footer">
          {successCount !== null ? (
            <button
              type="button"
              className="qa-modal-button qa-modal-button-primary"
              onClick={onClose}
            >
              Done
            </button>
          ) : confirming ? (
            <>
              <button
                type="button"
                className="qa-modal-button qa-modal-button-secondary"
                onClick={() => setConfirming(false)}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="qa-modal-button qa-modal-button-primary"
                onClick={handleSwap}
                disabled={isSwapDisabled}
              >
                {isSaving ? 'Updating…' : 'Confirm'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="qa-modal-button qa-modal-button-primary"
              onClick={() => setConfirming(true)}
              disabled={isSwapDisabled}
              title={
                !canUpdate
                  ? `You need 'user' or 'admin' permission to update. Current role: ${user?.role || 'none'}`
                  : ''
              }
            >
              Update Program Code
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
